import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as VehiclePhotoActions from '../actions/vehicle-photo.actions';
import { PhotoUploadService } from '../../service/photo-upload.service';
import { MatDialog } from '@angular/material/dialog';
import { VehiclePhotosService } from '../../service/vehicle-photos.service';
import { updateVehicleMainPhoto } from '../../../store';

@Injectable()
export class VehiclePhotoEffects {
  loadVehiclePhotos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehiclePhotoActions.loadVehiclePhotos),
      concatMap(({ vehicleId }) =>
        this.photoUploadService.getVehiclePhotos(vehicleId).pipe(
          map((photos) =>
            VehiclePhotoActions.loadVehiclePhotosSuccess({ photos }),
          ),
          catchError((error) =>
            of(VehiclePhotoActions.loadVehiclePhotosFailure({ error })),
          ),
        ),
      ),
    );
  });
  uploadVehiclePhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehiclePhotoActions.uploadVehiclePhoto),
      concatMap(({ sources, vehicleId }) =>
        this.photoUploadService.uploadPhoto(sources, vehicleId).pipe(
          mergeMap((vehiclePhotos) => {
            this.dialog.closeAll();

            this.vehiclePhotosService.clearTempFiles();
            vehiclePhotos = vehiclePhotos.map((photo) =>
              photo.previewImg
                ? photo
                : { ...photo, previewImg: photo.original },
            );
            const generalPhoto = vehiclePhotos.filter(
              (photo) => photo.isPrimary,
            );
            if (generalPhoto.length > 0) {
              return [
                updateVehicleMainPhoto({
                  photo: generalPhoto[0],
                  vehicleId,
                }),
                VehiclePhotoActions.uploadVehiclePhotoSuccess({
                  photos: vehiclePhotos,
                }),
              ];
            }
            return [
              VehiclePhotoActions.uploadVehiclePhotoSuccess({
                photos: vehiclePhotos,
              }),
            ];
          }),
          catchError((error) =>
            of(VehiclePhotoActions.loadVehiclePhotosFailure({ error })),
          ),
        ),
      ),
    );
  });

  deleteVehiclePhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehiclePhotoActions.deleteVehiclePhoto),
      concatMap(({ photo }) =>
        this.photoUploadService.deleteVehiclePhoto(photo).pipe(
          mergeMap(() => {
            this.dialog.closeAll();
            if (photo.isPrimary && photo.vehicle != null) {
              return [
                updateVehicleMainPhoto({
                  photo: null,
                  vehicleId: photo.vehicle,
                }),
                VehiclePhotoActions.deleteVehiclePhotoSuccess({ photo: photo }),
              ];
            }
            return [
              VehiclePhotoActions.deleteVehiclePhotoSuccess({
                photo: photo,
              }),
            ];
          }),
          catchError((error) =>
            of(VehiclePhotoActions.loadVehiclePhotosFailure({ error })),
          ),
        ),
      ),
    );
  });

  deleteSelectedVehiclePhotos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehiclePhotoActions.deleteSelectedVehiclePhotos),
      concatMap(({ photos }) =>
        this.photoUploadService.deleteVehiclePhotos(photos).pipe(
          concatMap(() => {
            const primaryPhotoVehicleId = photos.find(
              (photo) => photo.isPrimary,
            )?.vehicle;
            if (primaryPhotoVehicleId != null) {
              return [
                updateVehicleMainPhoto({
                  photo: null,
                  vehicleId: primaryPhotoVehicleId,
                }),
                VehiclePhotoActions.deleteSelectedVehiclePhotosSuccess({
                  photos,
                }),
              ];
            }
            return [
              VehiclePhotoActions.deleteSelectedVehiclePhotosSuccess({
                photos,
              }),
            ];
          }),
          catchError((error) =>
            of(VehiclePhotoActions.loadVehiclePhotosFailure({ error })),
          ),
        ),
      ),
    );
  });

  updateVehiclePhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VehiclePhotoActions.updateVehiclePhoto),
      concatMap(({ fields, photoId, vehicleId }) =>
        this.photoUploadService
          .updateVehiclePhoto(fields, photoId, vehicleId)
          .pipe(
            mergeMap((photo) => {
              if (photo.isPrimary) {
                return [
                  updateVehicleMainPhoto({
                    photo: photo,
                    vehicleId,
                  }),
                  VehiclePhotoActions.updateVehiclePhotoSuccess({
                    photo: photo,
                    fields,
                  }),
                ];
              }
              return [
                VehiclePhotoActions.updateVehiclePhotoSuccess({
                  photo: photo,
                  fields,
                }),
              ];
            }),
            catchError((error) =>
              of(VehiclePhotoActions.loadVehiclePhotosFailure({ error })),
            ),
          ),
      ),
    );
  });

  constructor(
    private actions$: Actions,
    private photoUploadService: PhotoUploadService,
    private dialog: MatDialog,
    private vehiclePhotosService: VehiclePhotosService,
  ) {}
}
